import { z } from "zod";

export const STORED_WORK_REQUEST_ATTACHMENT_FILENAME_MAX_LENGTH = 255;
export const WORK_REQUEST_ATTACHMENT_MAX_FILES = 5;
export const WORK_REQUEST_ATTACHMENT_MAX_BYTES = 50 * 1024 * 1024;
export const WORK_REQUEST_R2_KEY_PREFIX = "work-requests/";

const ALLOWED_VIDEO_MIME_TYPES = new Set([
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-msvideo",
]);

const ALLOWED_VIDEO_EXTENSIONS = new Set(["mp4", "webm", "mov", "avi"]);

export const storedWorkRequestAttachmentSchema = z.object({
  id: z.string().uuid(),
  filename: z.string().trim().min(1).max(
    STORED_WORK_REQUEST_ATTACHMENT_FILENAME_MAX_LENGTH,
    `Filename must be ${STORED_WORK_REQUEST_ATTACHMENT_FILENAME_MAX_LENGTH} characters or fewer`,
  ),
  r2Key: z
    .string()
    .min(1)
    .refine(
      (key) => key.startsWith(WORK_REQUEST_R2_KEY_PREFIX),
      "Attachment must be stored under the work-requests prefix",
    ),
  contentType: z.string().trim().min(1).max(255),
  sizeBytes: z
    .number()
    .int()
    .min(1)
    .max(WORK_REQUEST_ATTACHMENT_MAX_BYTES),
});

export const intakeAttachmentUploadRequestSchema = z.object({
  sessionId: z.string().uuid(),
  files: z
    .array(
      z.object({
        filename: z.string().trim().min(1).max(
          STORED_WORK_REQUEST_ATTACHMENT_FILENAME_MAX_LENGTH,
        ),
        contentType: z.string().trim().min(1).max(255),
        sizeBytes: z
          .number()
          .int()
          .min(1)
          .max(WORK_REQUEST_ATTACHMENT_MAX_BYTES),
      }),
    )
    .min(1)
    .max(WORK_REQUEST_ATTACHMENT_MAX_FILES),
});

export type StoredWorkRequestAttachment = {
  id?: string;
  filename: string;
  r2Key?: string;
  contentType?: string;
  sizeBytes?: number;
  /** Legacy in-database data URL. New writes must not include this. */
  dataUrl?: string;
};

export type WorkRequestR2Attachment = z.infer<
  typeof storedWorkRequestAttachmentSchema
>;

export function sanitizeWorkRequestAttachmentFilename(
  filename: string | null | undefined,
): string | null {
  if (!filename) return null;

  const basename = filename.split(/[\\/]/).pop() ?? "";
  const cleaned = basename
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/"/g, "")
    .trim()
    .slice(0, STORED_WORK_REQUEST_ATTACHMENT_FILENAME_MAX_LENGTH);

  return cleaned.length > 0 ? cleaned : null;
}

export function isAllowedWorkRequestAttachmentType(
  contentType: string,
  filename?: string,
): boolean {
  const normalized = contentType.toLowerCase().trim();
  if (normalized.startsWith("image/")) return true;
  if (normalized === "application/pdf") return true;
  if (ALLOWED_VIDEO_MIME_TYPES.has(normalized)) return true;

  const extension = filename?.split(".").pop()?.toLowerCase();
  return Boolean(extension && ALLOWED_VIDEO_EXTENSIONS.has(extension));
}

export function buildWorkRequestDraftR2Key(
  sessionId: string,
  attachmentId: string,
  filename: string,
): string {
  const safeName =
    sanitizeWorkRequestAttachmentFilename(filename) ?? "attachment.bin";
  return `${WORK_REQUEST_R2_KEY_PREFIX}drafts/${sessionId}/${attachmentId}-${safeName}`;
}

export function buildWorkRequestSubmittedR2Key(
  workRequestId: string,
  attachmentId: string,
  filename: string,
): string {
  const safeName =
    sanitizeWorkRequestAttachmentFilename(filename) ?? "attachment.bin";
  return `${WORK_REQUEST_R2_KEY_PREFIX}${workRequestId}/${attachmentId}-${safeName}`;
}

export type IntakeCategory =
  | "website_change"
  | "tech_support"
  | "creative_design"
  | "new_project"
  | "other";

export const INTAKE_CATEGORY_VALUES: IntakeCategory[] = [
  "website_change",
  "tech_support",
  "creative_design",
  "new_project",
  "other",
];

export function normalizeIntakeCategory(
  value: string | null | undefined,
): IntakeCategory | null {
  if (!value?.trim()) return null;

  const trimmed = value.trim();
  if (INTAKE_CATEGORY_VALUES.includes(trimmed as IntakeCategory)) {
    return trimmed as IntakeCategory;
  }

  const slug = trimmed
    .toLowerCase()
    .replace(/[/&]+/g, " ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

  if (INTAKE_CATEGORY_VALUES.includes(slug as IntakeCategory)) {
    return slug as IntakeCategory;
  }

  return null;
}

const CATEGORY_LABELS: Record<string, string> = {
  website_change: "Website Change",
  tech_support: "Tech Support",
  creative_design: "Creative Design",
  new_project: "New Project",
  other: "Other",
};

const HEADLINE_KEYS = [
  "changeSummary",
  "issueSummary",
  "projectSummary",
  "shortDescription",
  "assetType",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function formatScalarValue(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => formatScalarValue(item))
      .filter((item): item is string => Boolean(item));
    return parts.length > 0 ? parts.join(", ") : null;
  }

  return null;
}

export function getWorkRequestCategoryLabel(category?: string | null): string {
  if (!category) return "Work Request";
  return CATEGORY_LABELS[category] ?? humanizeKey(category);
}

export function getWorkRequestHeadline(data: {
  category?: string | null;
  company?: string | null;
  requestDetails?: unknown;
}): string {
  const details = isRecord(data.requestDetails) ? data.requestDetails : null;

  if (details) {
    for (const key of HEADLINE_KEYS) {
      const value = formatScalarValue(details[key]);
      if (value) return value;
    }
  }

  const categoryLabel = getWorkRequestCategoryLabel(data.category);
  if (data.company?.trim()) {
    return `${categoryLabel} request for ${data.company.trim()}`;
  }

  return categoryLabel;
}

/** Soft cap for email clients and email_logs; keeps subjects readable. */
export const WORK_REQUEST_TICKET_SUBJECT_MAX_LENGTH = 200;

function truncateWithEllipsis(value: string, maxLength: number): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  if (maxLength <= 3) return normalized.slice(0, maxLength);
  return `${normalized.slice(0, maxLength - 3).trimEnd()}...`;
}

/**
 * Build a ticket/email subject from a work request.
 * Truncates the headline so the full subject stays within an email-friendly limit.
 */
export function buildWorkRequestTicketSubject(data: {
  fullName?: string | null;
  category?: string | null;
  company?: string | null;
  requestDetails?: unknown;
  maxLength?: number;
}): string {
  const maxLength = data.maxLength ?? WORK_REQUEST_TICKET_SUBJECT_MAX_LENGTH;
  const fullName = data.fullName?.trim() || "Unknown";
  const categoryLabel = getWorkRequestCategoryLabel(data.category);
  const prefix = `[Intake] ${fullName}: ${categoryLabel} - `;
  const headline = getWorkRequestHeadline({
    category: data.category,
    company: data.company,
    requestDetails: data.requestDetails,
  });

  const availableForHeadline = Math.max(maxLength - prefix.length, 0);
  const truncatedHeadline = truncateWithEllipsis(
    headline,
    availableForHeadline,
  );
  const subject = `${prefix}${truncatedHeadline}`;

  return truncateWithEllipsis(subject, maxLength);
}

export function getWorkRequestSummaryItems(requestDetails: unknown): Array<{
  label: string;
  value: string;
}> {
  if (!isRecord(requestDetails)) return [];

  return Object.entries(requestDetails)
    .map(([key, value]) => {
      const formatted = formatScalarValue(value);
      if (!formatted) return null;
      return {
        label: humanizeKey(key),
        value: formatted,
      };
    })
    .filter((item): item is { label: string; value: string } => Boolean(item));
}

export function getWorkRequestSummaryText(
  requestDetails: unknown,
  limit = 3,
): string {
  return getWorkRequestSummaryItems(requestDetails)
    .slice(0, limit)
    .map((item) => `${item.label}: ${item.value}`)
    .join(" | ");
}

export function getWorkRequestScopeSnippet(
  aiScopeSummary?: string | null,
  limit = 2,
): string | null {
  if (!aiScopeSummary?.trim()) return null;

  const lines = aiScopeSummary
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, limit);

  return lines.length > 0 ? lines.join(" ") : null;
}
